/**
 * The Eingang (docs/18-vorratsbuch.md, Eingänge, E-2): each line of an Eingangsbeleg to an article
 * of the pantry, and what stays of it as a record. The order of the matching is that of E3: what
 * was learned from this supplier first, then the article of the same name, then the base product.
 * What fits none becomes a new article with the base product guessed from the name.
 */
import { lookup } from "@household-kit/food";
import { AISLES, DRINKS_AISLE, HOUSEHOLD_AISLES, baseProduct, suggestedKeep } from "./base-products.ts";
import type { BaseProduct } from "./base-products.ts";
import type { Entity } from "./entities.ts";
import type { Inbound, InboundLine } from "./inbound.ts";
import { FOOD_PROFILE, HOUSEHOLD_PROFILE, pantryName, pantrySize, suggestedCountBy } from "./pantry.ts";
import { fold } from "./search.ts";

// ---- base product from a name as suppliers write it ----

/**
 * The base product a supplier's name stands for, or undefined: food's `lookup` (household-kit),
 * which reads plurals, compounds („Steinchampignon“ → Champignon, „Himbeereis“ → Eis) and packing
 * in the name („Tomaten, stückig (Dose)“ → Dosentomaten), as the shopping list does. Drinks are
 * KellerLog's and left out.
 */
export function guessBaseProduct(text: string): BaseProduct | undefined {
  const p = lookup(text)?.product;
  return p && p.aisle !== DRINKS_AISLE ? p : undefined;
}

// ---- the record of an Eingang ----

export interface InboundRecordLine {
  /** As on the document. */
  text: string;
  name: string;
  /** The article it went to; null: not stored („nicht einlagern“). */
  article: string | null;
  /** Base units booked. */
  qty?: number;
  booking?: string;
}

/** An Eingang as booked, stored as master data of type `inbound`: the key against booking twice, and what was learned. */
export interface InboundRecord {
  id: string;
  key: string;
  format: string;
  kind: Inbound["kind"];
  supplier?: string;
  ref?: string;
  date?: string;
  file?: string;
  at: number;
  person?: string;
  lines: InboundRecordLine[];
  goods?: number;
  total?: number;
}

/** The record in a master data entity, or null if it is not one. */
export function inboundOf(e: Entity): InboundRecord | null {
  const f = e.fields;
  if (e.type !== "inbound" || typeof f.key !== "string" || typeof f.format !== "string" || typeof f.at !== "number") return null;
  const lines = Array.isArray(f.lines)
    ? (f.lines as InboundRecordLine[]).filter((l) => typeof l?.text === "string" && typeof l.name === "string" && (typeof l.article === "string" || l.article === null))
    : [];
  const str = (k: string) => (typeof f[k] === "string" ? { [k]: f[k] as string } : {});
  const num = (k: string) => (typeof f[k] === "number" ? { [k]: f[k] as number } : {});
  return {
    id: e.id,
    key: f.key,
    format: f.format,
    kind: f.kind === "order" ? "order" : "delivery",
    ...str("supplier"),
    ...str("ref"),
    ...str("date"),
    ...str("file"),
    at: f.at,
    ...str("person"),
    lines,
    ...num("goods"),
    ...num("total"),
  };
}

/**
 * What the Eingänge of one converter taught: the name as the supplier writes it → the article, or
 * null for „nicht einlagern“. The latest Eingang wins.
 */
export function learnedArticles(records: Iterable<InboundRecord>, format: string): Map<string, string | null> {
  const out = new Map<string, string | null>();
  for (const r of [...records].filter((r) => r.format === format).sort((a, b) => a.at - b.at)) {
    for (const l of r.lines) out.set(fold(l.name), l.article);
  }
  return out;
}

// ---- one line to an article ----

export interface PantryArticle {
  id: string;
  fields: Record<string, unknown>;
}

/**
 * How a line was matched: learned from this supplier, an article of the same name, the only
 * article of the base product (all sure); one of several of the base product (to check); none
 * (a new article); not to be stored (learned).
 */
export type MatchState = "learned" | "name" | "base" | "choice" | "new" | "skip";

export interface LineMatch {
  state: MatchState;
  /** The article proposed; null for a new one or none. */
  article: string | null;
  /** Articles of the same base product, for the choice. */
  candidates: string[];
  /** The base product guessed from the name, for a new article. */
  base?: string;
}

export const isSure = (s: MatchState) => s === "learned" || s === "name" || s === "base";

export function matchInboundLine(line: InboundLine, articles: readonly PantryArticle[], learned: ReadonlyMap<string, string | null>): LineMatch {
  // A table may name the base product; otherwise it comes from the name.
  const base = (typeof line.fields?.base === "string" ? line.fields.base : undefined) ?? guessBaseProduct(line.name)?.id;
  const ofBase = base ? articles.filter((a) => a.fields.base === base).map((a) => a.id) : [];
  const withBase = base ? { base } : {};
  const key = fold(line.name);
  if (learned.has(key)) {
    const id = learned.get(key)!;
    if (id === null) return { state: "skip", article: null, candidates: ofBase, ...withBase };
    if (articles.some((a) => a.id === id)) return { state: "learned", article: id, candidates: ofBase, ...withBase };
  }
  const named = articles.filter((a) => [a.fields.product, a.fields.name].some((n) => typeof n === "string" && fold(n) === key));
  if (named.length === 1) return { state: "name", article: named[0]!.id, candidates: ofBase, ...withBase };
  // The only article of the base product is sure when the line names just the base product („Honig“);
  // another name („Waldhonig“ for the Blütenhonig) is to check, a new article the other choice (B17).
  const generic = base ? (baseProduct(base)?.names ?? []).some((n) => fold(n) === key) : false;
  if (ofBase.length === 1) return { state: generic ? "base" : "choice", article: ofBase[0]!, candidates: ofBase, ...withBase };
  if (ofBase.length > 1) return { state: "choice", article: ofBase[0]!, candidates: ofBase, ...withBase };
  return { state: "new", article: null, candidates: [], ...withBase };
}

/**
 * The quantity of a line in the article's base unit, or null if it cannot be said: 466 g of loose
 * pears for an article in g; 1 pack „Ganzkornbrot 1 kg“ for an article in g; but 465 g of bananas
 * for an article counted in pieces only the person knows.
 */
export function lineQty(line: InboundLine, f: Record<string, unknown>): number | null {
  const unit = typeof f.unit === "string" && f.unit !== "" ? f.unit : "Stk.";
  if (line.total && line.total.unit === unit) return line.total.qty;
  const size = pantrySize(f);
  if (line.packs !== null && size !== null) return Math.round(line.packs * size);
  return null;
}

/** The fields of a new article for a line: the name as the supplier writes it, the guessed base product, the unit from the line. */
export function newArticleFields(line: InboundLine, base: BaseProduct | undefined): Record<string, unknown> {
  const profile = base && HOUSEHOLD_AISLES.includes(base.aisle) ? HOUSEHOLD_PROFILE : FOOD_PROFILE;
  const aisle = base ? AISLES.find((a) => a.id === base.aisle)?.name : undefined;
  const fields: Record<string, unknown> = {
    profile: profile.id,
    product: line.name,
    unit: line.total?.unit ?? "Stk.",
    ...(base ? { base: base.id } : {}),
    ...(aisle ? { aisle, category: aisle } : {}),
    // The content of one pack: „Ganzkornbrot 1 kg“, a box of 6 eggs.
    ...(line.packs !== null && line.content ? { size: line.content.qty } : {}),
    ...(line.organic && profile === FOOD_PROFILE ? { organic: true } : {}),
    // How it is kept, from its aisle (C3); it says where it goes (A5).
    ...(profile === FOOD_PROFILE && suggestedKeep(base) ? { keep: suggestedKeep(base) } : {}),
    // Eggs: a box of six, eaten one by one.
    ...((line.total?.unit ?? "Stk.") === "Stk." && suggestedCountBy(base) ? { countBy: suggestedCountBy(base) } : {}),
    tracking: profile.tracking,
    // What a table says beyond the quantity: brand, container, restock rule, barcodes.
    ...line.fields,
  };
  return { ...fields, name: pantryName(fields) };
}

/** When the goods came in: the day of delivery at noon, if that lies in the past; else now. */
export function inboundAt(doc: Pick<Inbound, "date">, now: number): number {
  if (!doc.date) return now;
  const [y, m, d] = doc.date.split("-").map(Number);
  const noon = new Date(y!, m! - 1, d!, 12).getTime();
  return Number.isFinite(noon) && noon < now ? noon : now;
}
